import { Volume } from '@/api/type-server'
import { Trans } from '@/components'
import { BYTES_PER_GB, formatStorageGB, getEffectiveQuota, toBytes } from '@/utils/storage'
import { cn } from '@/utils/style'

interface VolumeCardProps {
  volume: Volume
  ownerName: string
  domainName: string
  showOwner?: boolean
  isExpired?: boolean
}

export function VolumeCard({
  volume,
  ownerName,
  domainName,
  showOwner = true,
  isExpired = false,
}: VolumeCardProps) {
  // Calculate effective quota based on expiration status
  const effectiveQuotaGB = getEffectiveQuota(volume, isExpired)

  // Calculate usage percentage
  const usageBytes = volume.usage_bytes || toBytes(volume.usage_mb || 0, 'mb')
  const quotaBytes = effectiveQuotaGB * BYTES_PER_GB
  const usagePercent = quotaBytes > 0 ? (usageBytes / quotaBytes) * 100 : 0
  const usageGB = formatStorageGB(usageBytes, { includeUnit: false })

  // Determine color and warning message based on usage percentage
  let progressBarColorClassName = 'bg-btn-secondary' // Blue (0-79%)
  let warningMessage = ''
  let warningTextColor = ''

  if (usagePercent >= 95) {
    // Red (95-100%)
    progressBarColorClassName = 'bg-error'
    warningMessage = 'space.volumeItemNotAvailable'
    warningTextColor = 'text-error'
  } else if (usagePercent >= 80) {
    // Orange (80-94%)
    progressBarColorClassName = 'bg-warning'
    warningMessage = 'space.volumeItemLowStorage'
    warningTextColor = 'text-warning'
  }

  return (
    <div className="bg-panel-white border border-gray300 rounded-lg p-6 flex flex-col gap-3">
      {/* Header: Region name + quota + progress bar */}
      <div className="flex items-start justify-between">
        {/* Left: Region name and quota */}
        <div className="flex flex-col gap-1 w-[120px] shrink-0">
          <div className="h6">{effectiveQuotaGB}GB</div>
          <div className="t-sm text-secondary">{domainName}</div>
        </div>

        {/* Right: Progress bar and usage info */}
        <div className="flex-1 flex flex-col gap-1.5 min-w-0">
          {/* Progress bar */}
          <div className="flex flex-col py-2.5">
            <div className="bg-neutral-50 border border-neutral-200 h-1.5 rounded-full overflow-hidden">
              <div
                className={cn('h-full rounded-full transition-all', progressBarColorClassName)}
                style={{
                  width: `${Math.min(usagePercent, 100)}%`,
                }}
              />
            </div>
          </div>

          {/* Usage text */}
          <div className="flex items-center justify-between t-sb">
            <div className="flex items-center gap-1 text-secondary t-sm">
              <Trans
                i18nKey="space.usageFormat"
                values={{
                  usage: usageGB,
                  quota: effectiveQuotaGB,
                }}
                components={[
                  <span className="text-primary" key="usage" />,
                  <span className="text-primary" key="quota" />,
                ]}
              />
              <span className="text-tertiary">{usagePercent.toFixed(2)}%</span>
            </div>
          </div>

          {/* Warning message */}
          {warningMessage && (
            <div className={cn('t-sb', warningTextColor)}>
              <Trans i18nKey={warningMessage} />
            </div>
          )}
        </div>
      </div>

      {/* Owner information section - conditionally rendered */}
      {showOwner && (
        <div className="bg-panel-gray-100 rounded-lg p-4 flex flex-col gap-4">
          {/* Origin Owner and Current Owner */}
          <div className="flex items-center gap-4">
            {/* Origin Owner */}
            <div className="flex-1 flex flex-col gap-1">
              <div className="t-xs text-secondary">
                <Trans i18nKey="teamData.detail.originOwner">Origin Owner</Trans>
              </div>
              <div className="t-sb text-primary">{ownerName}</div>
            </div>

            {/* Current Owner */}
            <div className="flex-1 flex flex-col gap-1">
              <div className="t-xs text-secondary">
                <Trans i18nKey="teamData.detail.currentOwner">Current Owner</Trans>
              </div>
              <div className="t-sb text-primary">{ownerName}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
